/**
 * ShiftLegend — Dynamic shift legend with hover popovers showing hours per shift.
 * Updates when campus changes (campus-specific shifts vs corporate defaults).
 *
 * Props:
 *   shiftData — array of { shift_name, hour_of_day } from core_shift_hours or att_shift_default_hours
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import { hourToLocalizedHour } from '../../../../libs.jsx';

const SHIFT_COLORS = {
    Day: '#27B4FF',
    Evening: '#276BFF',
    Night: '#0B16FF',
};

const SHIFT_ORDER = ['Day', 'Evening', 'Night'];

const ShiftLegendItem = ({ name, color, hours }) => {
    const [open, setOpen] = useState(false);
    const [style, setStyle] = useState({});
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    const handleEnter = () => {
        if (ref.current) {
            const rect = ref.current.getBoundingClientRect();
            setStyle({ position: 'fixed', top: `${rect.bottom + 6}px`, left: `${rect.left}px`, zIndex: 9999 });
        }
        setOpen(true);
    };

    return (
        <div ref={ref} onMouseEnter={handleEnter} onMouseLeave={() => setOpen(false)}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', cursor: 'default', position: 'relative' }}>
            <span style={{ width: '12px', height: '12px', borderRadius: '2px', background: color, display: 'inline-block', flexShrink: 0 }} />
            {name}
            {open && hours.length > 0 ? (
                <div style={{
                    ...style,
                    background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)',
                    padding: 'var(--space-3) var(--space-4)', minWidth: '140px',
                }}>
                    <div style={{ fontWeight: 600, fontSize: 'var(--font-size-xs)', marginBottom: 'var(--space-2)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: color, display: 'inline-block' }} />
                        {name} Shift
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-primary)' }}>
                        {hours.sort((a, b) => a - b).map((h) => (
                            <span key={h}>{hourToLocalizedHour(h)}</span>
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
};

const ShiftLegend = ({ shiftData }) => {
    const shifts = useMemo(() => {
        const grouped = {};
        (shiftData || []).forEach((item) => {
            const name = item.shift_name;
            if (!grouped[name]) grouped[name] = [];
            grouped[name].push(item.hour_of_day);
        });
        return SHIFT_ORDER
            .filter((name) => grouped[name]?.length > 0)
            .map((name) => ({ name, color: SHIFT_COLORS[name] || '#888', hours: grouped[name] }));
    }, [shiftData]);

    if (shifts.length === 0) return null;

    return (
        <div style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'center' }}>
            {shifts.map((s) => (
                <ShiftLegendItem key={s.name} name={s.name} color={s.color} hours={s.hours} />
            ))}
        </div>
    );
};

export default ShiftLegend;
